import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { theme } from '../../../theme/theme';

const useDataMultiDisplayCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 20,
        gap: 24,
        borderRadius: 5,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: theme.colors.darkBlue[2],
        '& > span': {
            color: theme.colors.white,
            ...theme.typography.headings.h6,
            marginBottom: 0,
            textTransform: 'uppercase',
        },
    },
    stats: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 5,
    },
    stat: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        '& > span:first-child': {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: theme.colors.neutral[3],
            ...theme.typography.content.caption.paragraph,
            textTransform: 'capitalize',
            '& > div': {
                width: 8,
                height: 8,
                borderRadius: 2,
            },
        },
        '& > span:nth-child(2)': {
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            color: theme.colors.white,
            ...theme.typography.displays.d2,
            '& > span:last-child': {
                ...theme.typography.content.label.default,
            },
        },
        '& > span:last-child': {
            color: theme.colors.white,
            ...theme.typography.content.caption.paragraph,
        },
    },
    chart: {
        minHeight: 50,
        flex: 1,
    },
});

/**
 * Display information about data aggregations of the same population.
 * All data groups must be mutually exclusive with each other.
 *
 * @param props.fetching show fetching skeletons
 * @param props.name card name
 * @param props.data data groups to display, name is the header, main is the center text and info the bottom text, color
 *        is a list of one or two strings to compute a linear gradient
 * @param props.mode the widget to use at the bottom of the card
 * @param props.tile custom options for the tile widget
 * @param props.bar custom options for the bar widget
 */
export const DataMultiDisplayCard = (props: {
    fetching?: boolean;
    name: string;
    data: { color: [string, string?]; name: string; main: string; info: string; ratio: number }[];
    mode: 'tile' | 'bar';
    tile?: { edge?: number; gap?: number; fill?: boolean };
    bar?: { background?: string };
}) => {
    const classes = useDataMultiDisplayCardClasses();
    const cumulativeRatios: number[] = Array(props.data.length).fill(0);
    props.data.forEach(({ ratio }, i) => (cumulativeRatios[i] = ratio + (i != 0 ? cumulativeRatios[i - 1] : 0)));
    const colors = cumulativeRatios.map((cumulativeRatio, i) => [cumulativeRatio, props.data[i].color] as const);
    const map = (i: number, j: number, count: { x: number; y: number }) => {
        const ratio = (i * count.y + j) / (count.x * count.y);
        return colors.find(([v]) => v > ratio)?.[1][0] ?? '';
    };

    if (props.fetching)
        return (
            <div className={classes.root}>
                <Skeleton />
                <div className={classes.stats}>
                    <Skeleton className={classes.stat} variant='rect' height={65} />
                    <Skeleton className={classes.stat} variant='rect' height={65} />
                </div>
                <Skeleton variant='rect' className={classes.chart} />
            </div>
        );

    return (
        <div className={classes.root}>
            <span>{props.name}</span>
            <div className={classes.stats}>
                {props.data.map((stat, i) => {
                    const background = `linear-gradient(${stat.color[0]}, ${stat.color[1 % stat.color.length]})`;
                    return (
                        <div key={i} className={classes.stat}>
                            <span>
                                <div style={{ background }} />
                                {stat.name}
                            </span>
                            <span>
                                <span>{`${stat.main} `.slice(0, stat.main.indexOf(' '))}</span>
                                <span>{`${stat.main} `.slice(stat.main.indexOf(' '))}</span>
                            </span>
                            <span>{stat.info}</span>
                        </div>
                    );
                })}
            </div>
            {props.mode === 'tile' ? (
                <HeatMap className={classes.chart} edge={4} gap={1} {...props.tile} map={map} />
            ) : (
                <MultiBar className={classes.chart} bars={props.data} {...props.bar} />
            )}
        </div>
    );
};

/**
 * HeatMap component.
 *
 * @param props.edge size of tile
 * @param props.gap space between tiles
 * @param props.map function that takes tile position and count and returns a color
 */
export const HeatMap = (
    props: JSX.IntrinsicElements['div'] & {
        edge: number;
        gap: number;
        map: (i: number, j: number, count: { x: number; y: number }) => string;
    },
) => {
    const { edge, gap, map, ...rootProps } = props;
    const canvas$ = React.useRef<HTMLCanvasElement>();

    React.useLayoutEffect(() => {
        if (!canvas$.current) return;
        render(canvas$.current, edge, gap, map);
        const interval = setInterval(() => {
            if (
                canvas$.current?.width === canvas$.current!.clientWidth &&
                canvas$.current?.height === canvas$.current!.clientHeight
            )
                return;
            render(canvas$.current!, edge, gap, map);
        }, 500);
        return () => clearInterval(interval);
    }, [canvas$.current, edge, gap, map]);

    const render = (canvas$: HTMLCanvasElement, edge: number, gap: number, map: any) => {
        const context = canvas$.getContext('2d', { desynchronized: true });
        if (!context) return;
        const size = { x: canvas$.clientWidth, y: canvas$.clientHeight };
        const fit = { x: size.x / (edge + gap), y: size.y / (edge + gap) };
        const count = { x: Math.floor(fit.x), y: Math.floor(fit.y) };
        canvas$.width = size.x;
        canvas$.height = size.y;
        context.clearRect(0, 0, context.canvas.width, context.canvas.height);
        for (let i = 0, x = 0; i < count.x; i++, x += edge + gap)
            for (let j = 0, y = 0; j < count.y; j++, y += edge + gap) {
                context.fillStyle = map(i, j, count);
                context.fillRect(x, y, edge, edge);
            }
    };

    return (
        <div {...rootProps} style={{ ...rootProps.style, position: 'relative', padding: 0, margin: 0 }}>
            <canvas ref={canvas$ as any} style={{ position: 'absolute', width: '100%', height: '100%' }} />;
        </div>
    );
};

/**
 * MultiBar component.
 *
 * @param props.bars bar parts containing their ration and color
 * @param props.background bar background color, displayed when the sum of the bars ratios is smaller than 1
 */
export const MultiBar = (
    props: JSX.IntrinsicElements['div'] & { bars: { ratio: number; color: [string, string?] }[]; background?: string },
) => {
    const { bars, ...rootProps } = props;
    const gradients: string[] = [];
    let start = 0;
    bars.forEach(({ ratio, color }) =>
        gradients.push(`${color[0]} ${start * 100}%, ${color[1 % color.length]} ${(start += ratio) * 100}%`),
    );
    const background = `linear-gradient(90deg, ${gradients.join(',')}, ${props.background ?? 'white'} ${start * 100}%)`;

    return (
        <div {...rootProps} style={{ ...rootProps.style, display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background }} />
        </div>
    );
};
